import { useState, useEffect, ReactElement } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import GavelIcon from "@mui/icons-material/Gavel";
import { db } from "../firebase";
import { collection, addDoc, deleteDoc, doc, onSnapshot, query, orderBy, Unsubscribe, serverTimestamp, FirestoreError } from "firebase/firestore";
import { isDemoMode } from "../utils/demoMode";
import { isBypassAuthEnabled } from "../utils/bypassAuth";
import { User } from "firebase/auth";

interface TestItem {
  id: string;
  text: string;
  createdAt: any;
}

interface TestPageProps {
  workspaceId: string;
  onTestTermsDialog?: () => void;
}

function TestPage({ workspaceId, onTestTermsDialog }: TestPageProps): ReactElement {
  const [items, setItems] = useState<TestItem[]>([]);
  const [newItem, setNewItem] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [adding, setAdding] = useState<boolean>(false);

  // Real-time listener for test items
  useEffect(() => {
    if (isDemoMode()) {
      setLoading(false);
      return;
    }

    const testItemsRef = collection(db, "workspaces", workspaceId, "testItems");
    const q = query(testItemsRef, orderBy("createdAt", "desc"));

    let unsubscribe: Unsubscribe | undefined;

    const handleSnapshot = (snapshot: any): void => {
      const itemsList: TestItem[] = snapshot.docs.map((doc: any) => ({
        id: doc.id,
        text: doc.data().text as string,
        createdAt: doc.data().createdAt,
      }));
      setItems(itemsList);
      setLoading(false);
    };

    const handleError = (error: FirestoreError): void => {
      console.error("Error fetching test items:", error);
      // If index is missing, try query without orderBy
      if (error.code === "failed-precondition" || error.message?.includes("index")) {
        console.log("Index missing, trying query without orderBy");
        const fallbackQ = query(testItemsRef);
        unsubscribe = onSnapshot(
          fallbackQ,
          handleSnapshot,
          (fallbackError: FirestoreError) => {
            console.error("Fallback query also failed:", fallbackError);
            setLoading(false);
          }
        );
      } else {
        setLoading(false);
      }
    };

    try {
      unsubscribe = onSnapshot(q, handleSnapshot, handleError);
    } catch (error) {
      console.error("Query setup error:", error);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId]);

  const handleAddItem = async (): Promise<void> => {
    if (!newItem.trim() || adding || isDemoMode()) return;

    setAdding(true);
    try {
      const testItemsRef = collection(db, "workspaces", workspaceId, "testItems");
      await addDoc(testItemsRef, {
        text: newItem.trim(),
        createdAt: serverTimestamp(),
      });
      setNewItem("");
    } catch (error) {
      console.error("Error adding test item:", error);
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteItem = async (itemId: string): Promise<void> => {
    if (isDemoMode()) return;

    try {
      const itemRef = doc(db, "workspaces", workspaceId, "testItems", itemId);
      await deleteDoc(itemRef);
    } catch (error) {
      console.error("Error deleting test item:", error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Test Page
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add items here to test real-time updates. Items will appear automatically on all devices when added.
      </Typography>

      {/* Dev-only: Terms Dialog Test */}
      {isBypassAuthEnabled() && onTestTermsDialog && (
        <>
          <Paper sx={{ p: 2, mb: 3, bgcolor: "action.hover" }}>
            <Stack spacing={2}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Development Testing Tools
                </Typography>
                <Typography variant="body2">
                  Use these tools to test features that normally require authentication.
                </Typography>
              </Alert>
              <Button
                variant="outlined"
                startIcon={<GavelIcon />}
                onClick={onTestTermsDialog}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
              >
                Test Terms of Service Dialog
              </Button>
              <Typography variant="caption" color="text.secondary">
                This will show the terms acceptance dialog that new users see during registration.
              </Typography>
            </Stack>
          </Paper>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="New Item"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !adding) {
                handleAddItem();
              }
            }}
            fullWidth
            size="small"
            disabled={adding || isDemoMode()}
          />
          <Button
            variant="contained"
            startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
            onClick={handleAddItem}
            disabled={!newItem.trim() || adding || isDemoMode()}
          >
            {adding ? "Adding..." : "Add"}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ flex: 1, overflow: "auto" }}>
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No items yet. Add an item above to see it appear here in real-time.
            </Typography>
          </Box>
        ) : (
          <List>
            {items.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteItem(item.id)}
                    color="error"
                    disabled={isDemoMode()}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}

export default TestPage;
